import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Sparkle, Heart } from "../doodles/Doodles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TILE_COLORS = ["#FFE7F1", "#E7F4FF", "#FFF7DC", "#EAFBE7", "#FCE4FF", "#FFE4D6"];

export default function FeaturedTiles() {
  const [tiles, setTiles] = useState([]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/custom-tiles?section=home`)
      .then((r) => setTiles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTiles([]));
  }, []);

  if (!tiles.length) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const tile = el.querySelector("[data-tile]");
    const step = tile ? tile.clientWidth + 16 : el.clientWidth * 0.7;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const isExternal = (link) => /^https?:\/\//i.test(link || "");

  return (
    <section className="section-y relative overflow-hidden" data-testid="featured-tiles-section">
      <Sparkle color="#FFD93D" className="absolute top-8 left-10 w-6 animate-twinkle pointer-events-none"/>
      <Heart color="#FF6B9D" className="absolute bottom-10 right-14 w-7 animate-bounce-doodle pointer-events-none"/>

      <div className="container-x">
        <div className="text-center mb-6">
          <p className="label-small text-xs sm:text-sm">✨ Featured Collections ✨</p>
          <h2 className="font-doodle text-2xl sm:text-3xl mt-1 text-[#2D2424]">Explore <span className="text-[#FF6B9D]">more</span> from us</h2>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            data-testid="featured-tiles-prev"
            aria-label="scroll left"
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full border-2 border-[#2D2424] bg-white hover:bg-[#FFE7F1] items-center justify-center"
            style={{ boxShadow: "3px 3px 0 #2D2424" }}
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2}/>
          </button>

          <div
            ref={scrollerRef}
            className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 sm:px-12"
            style={{ scrollbarWidth: "none" }}
          >
            {tiles.map((t, i) => {
              const bg = TILE_COLORS[i % TILE_COLORS.length];
              const inner = (
                <>
                  <div className="aspect-square overflow-hidden bg-white/40">
                    <img
                      src={t.image}
                      alt={t.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-2.5 sm:p-3">
                    <div className="flex items-center justify-between gap-1">
                      <h3 className="font-doodle text-sm sm:text-base lg:text-lg text-[#2D2424] leading-tight">{t.title}</h3>
                      <ArrowUpRight className="w-4 h-4 text-[#7A1F2B] group-hover:rotate-45 transition-transform shrink-0" strokeWidth={2}/>
                    </div>
                    {t.subtitle && <p className="font-hand text-[11px] sm:text-xs text-[#5C5555] mt-1 leading-tight">{t.subtitle}</p>}
                  </div>
                </>
              );
              const commonProps = {
                "data-tile": true,
                "data-testid": `featured-tile-${t.id}`,
                className: "group doodle-card overflow-hidden flex flex-col snap-start shrink-0 w-[44%] sm:w-[28%] lg:w-[18%]",
                style: { backgroundColor: bg },
              };
              return isExternal(t.link) ? (
                <a key={t.id} href={t.link} target="_blank" rel="noreferrer" {...commonProps}>{inner}</a>
              ) : (
                <Link key={t.id} to={t.link || "/"} {...commonProps}>{inner}</Link>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => scrollBy(1)}
            data-testid="featured-tiles-next"
            aria-label="scroll right"
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full border-2 border-[#2D2424] bg-white hover:bg-[#FFE7F1] items-center justify-center"
            style={{ boxShadow: "3px 3px 0 #2D2424" }}
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2}/>
          </button>
        </div>
      </div>
    </section>
  );
}
